import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import Filters from './Filters'
import VoucherTable from './VoucherTable'
import { useEffect, useState } from 'react'
import type { VoucherFilters } from './types'
import useVouchers from './useVouchers'
import FloatingActionBtn from 'ui/components/FloatingActionBtn'
import VoucherDialog from './VoucherDialog'
import { useSelectedFinanceCompany } from 'ui/ManagerFinance/hooks/useSelectedFinanceCompany'
import LoadingState from 'ui/components/LoadingState'
import useVats from './useVats'

const Container = styled.div`
	padding: 16px;
`

const ITEMS_PER_PAGE = 25

const VoucherView = () => {
	const intl = useIntl()

	const [addDialogVisible, setDialogVisible] = useState<boolean>(false)

	const selectedVoucherId = useSelector((state: any) => {
		const idStr = state.router?.location?.query?.id
		return idStr ? parseInt(idStr, 10) : undefined
	})

	const [filters, setFilters] = useState<VoucherFilters>({
		limit: ITEMS_PER_PAGE,
		offset: 0,
		orderByColumn: 'id',
		orderByDirection: 'desc',
	})

	const { company, loading: loadingCompany } = useSelectedFinanceCompany()
	const {
		vouchers,
		totalRecords,
		loading: loadingVouchers,
		refresh: refreshVouchers,
	} = useVouchers({
		companyUUID: company?.uuid,
		databaseSource: company?.integrations?.viiluTalous?.databaseSource,
		filters,
	})
	const {
		vats,
		loading: loadingVats,
		refresh: refreshVats,
	} = useVats({
		companyUUID: company?.uuid,
		databaseSource: company?.integrations?.viiluTalous?.databaseSource,
	})

	const refresh = async () => {
		await Promise.allSettled([refreshVouchers(), refreshVats()])
	}

	useEffect(() => {
		// When navigating back we need to force refresh
		// this is a bit hacky
		if (!selectedVoucherId) {
			refresh()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedVoucherId])

	const loading =
		(vouchers.length === 0 && loadingVouchers) || loadingCompany || loadingVats

	const showDialog = () => {
		setDialogVisible(true)
	}

	const hideDialog = () => {
		refresh()
		setDialogVisible(false)
	}

	const renderFAB = () => {
		return (
			<FloatingActionBtn
				iconName={'add'}
				text={intl.formatMessage({
					defaultMessage: 'LUO TOSITE',
					description: 'Create voucher',
				})}
				action={showDialog}
			/>
		)
	}

	if (loading) {
		return (
			<div className="full-width full-height flex-center">
				<LoadingState />
			</div>
		)
	}

	return (
		<Container>
			<Filters filters={filters} setFilters={setFilters} />
			<VoucherTable
				filters={filters}
				itemsPerPage={ITEMS_PER_PAGE}
				setFilters={setFilters}
				totalItemCount={totalRecords}
				loading={loadingVouchers}
				vouchers={vouchers}
			/>

			{selectedVoucherId && (
				<VoucherDialog
					companyUUID={company?.uuid}
					databaseSource={company?.integrations?.viiluTalous?.databaseSource}
					voucherId={selectedVoucherId}
					vats={vats}
					refresh={refresh}
					onHideURL={'/manager/finance/accounting'}
				/>
			)}

			{addDialogVisible && (
				<VoucherDialog refresh={refresh} onHide={hideDialog} />
			)}
			{renderFAB()}
		</Container>
	)
}

export default VoucherView
